<template>
  <!--
    The `value` attribute is sp-select's programmatic set channel: a clear or a URL restore lands,
    and Vue leaves the live selection alone between writes.
  -->
  <sp-select
    class="availability-visit-type-select"
    :aria-label="prompt"
    :items.prop="items"
    :value.attr="value"
    :prompt="prompt"
    :loading="loading"
    :disabled="loading"
    :data-testid="TestId.finder.availabilityVisitType"
    @input="handleInput"
  ></sp-select>
</template>

<script setup>
import { computed } from "vue";
import { firstDetail } from "#ce/utils/events";
import { emptyToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";

const emit = defineEmits(["select"]);

const props = defineProps({
  /** The selected row value: the comma-joined id list `visit_types` carries. */
  value: {
    type: String,
    default: undefined,
  },
  /**
   * The visit-type tree `useVisitTypeFilter` builds: `[{ title, value, children? }]`, its clearing
   * row included. The clearing row is dropped here in favour of the prompt.
   */
  nodes: {
    type: Array,
    default: () => [],
  },
  /** The row value that clears the selection. */
  anyValue: {
    type: String,
    required: true,
  },
  prompt: {
    type: String,
    required: true,
  },
  /** Whether the rows are being fetched. The select is disabled meanwhile, so a row a refresh may drop cannot be picked. */
  loading: {
    type: Boolean,
    default: false,
  },
});

// Category rows lead the list; each category's named visit types follow under a header carrying
// the category's title.
const items = computed(() => {
  const categories = props.nodes.filter((node) => node.value !== props.anyValue);
  const rows = categories.map(({ title, value }) => ({ title, value }));

  for (const category of categories) {
    for (const child of category.children ?? []) {
      rows.push({ title: child.title, value: child.value, group: category.title });
    }
  }

  return rows;
});

function handleInput(event) {
  emit("select", emptyToUndefined(firstDetail(event)));
}
</script>

<style lang="scss" scoped>
.availability-visit-type-select {
  display: block;
  width: 100%;
}
</style>
